import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle, CheckCircle, Plus, Minus } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

// Modal for adjusting client balance - uses createPortal for proper z-index handling on mobile
const AdjustBalanceModal = ({ isOpen, onClose, client, onSuccess }) => {
  console.log('AdjustBalanceModal render - isOpen:', isOpen, 'client:', client?.id);
  const [type, setType] = useState('income'); // 'income' or 'expense'
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Ensure client balance is a number
  const currentBalance = parseFloat(client?.current_balance || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!amount || parseFloat(amount) <= 0) {
      setError('El monto debe ser mayor a 0');
      return;
    }

    if (!description.trim()) {
      setError('La descripción es requerida');
      return;
    }

    setLoading(true);

    try {
      if (!client?.id) {
        setError('Cliente no disponible');
        return;
      }

      const response = await api.post(`/users/${client.id}/adjust-balance`, {
        amount: parseFloat(amount),
        type: type,
        description: description.trim()
      });

      toast.success(`Saldo ajustado: $${parseFloat(amount).toLocaleString()}`);

      // Limpiar formulario
      setAmount('');
      setDescription('');
      setType('income');

      // Llamar callback de éxito
      if (onSuccess) {
        onSuccess(response.data);
      }

      // Cerrar modal
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Error al ajustar el saldo';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!client) return null;

  const amountValue = parseFloat(amount || 0);
  const newBalance = currentBalance + (type === 'income' ? amountValue : -amountValue);

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/50 dark:bg-black/70"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white dark:bg-dark-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-700 flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Ajustar Saldo
              </h2>
              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Body */}
            <form id="adjustBalanceForm" onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-6 space-y-6">
              {/* Cliente Info */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-1">
                  Cliente
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {client.full_name}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {client.email}
                </p>
                <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                  <p className="text-xs text-blue-600 dark:text-blue-400 font-semibold uppercase mb-1">
                    Saldo Actual
                  </p>
                  <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                    ${currentBalance.toLocaleString('es-ES', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </p>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-start space-x-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              )}

              {/* Type Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  Tipo de Operación
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setType('income')}
                    className={`p-3 rounded-lg border-2 transition flex items-center justify-center space-x-2 font-medium ${
                      type === 'income'
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : 'border-gray-200 dark:border-dark-700 text-gray-600 dark:text-gray-400 hover:border-green-200'
                    }`}
                  >
                    <Plus className="w-5 h-5" />
                    <span>Ingreso</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setType('expense')}
                    className={`p-3 rounded-lg border-2 transition flex items-center justify-center space-x-2 font-medium ${
                      type === 'expense'
                        ? 'border-red-500 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                        : 'border-gray-200 dark:border-dark-700 text-gray-600 dark:text-gray-400 hover:border-red-200'
                    }`}
                  >
                    <Minus className="w-5 h-5" />
                    <span>Egreso</span>
                  </button>
                </div>
              </div>

              {/* Amount Input */}
              <div>
                <label htmlFor="amount" className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Monto $
                </label>
                <input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setError('');
                  }}
                  className="w-full px-4 py-2 border border-gray-200 dark:border-dark-700 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Descripción/Observación
                </label>
                <textarea
                  id="description"
                  placeholder="Motivo del ajuste (ej: Reembolso por error, Compensación, etc.)"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    setError('');
                  }}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-200 dark:border-dark-700 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                />
              </div>

              {/* Preview */}
              {amount && (
                <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Saldo Actual:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      ${currentBalance.toLocaleString('es-ES', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {type === 'income' ? 'Ingreso:' : 'Egreso:'}
                    </span>
                    <span className={`font-semibold ${type === 'income' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {type === 'income' ? '+' : '-'}${amountValue.toLocaleString('es-ES', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200 dark:border-dark-600 flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">Nuevo Saldo:</span>
                    <span className={`text-lg font-bold ${newBalance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      ${newBalance.toLocaleString('es-ES', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </span>
                  </div>
                </div>
              )}

            </form>

            {/* Buttons Footer */}
            <div className="flex space-x-3 p-6 border-t border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800 flex-shrink-0 rounded-b-lg">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-200 dark:border-dark-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading || !amount || !description}
                form="adjustBalanceForm"
                className={`flex-1 px-4 py-2 bg-gradient-primary text-white rounded-lg font-medium transition disabled:opacity-50 flex items-center justify-center space-x-2 ${
                  loading ? 'cursor-not-allowed' : 'hover:shadow-lg'
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Procesando...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>Ajustar Saldo</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return modalContent;
};

export default AdjustBalanceModal;
